/**
 * Listener effects for the Assistant WS channel.
 *
 * The assistant-api pushes lightweight `Invalidate` frames when backend data
 * changes (e.g. an exchange changelog sync completes), and, for entities that
 * run mutations through its async job engine (SKY-4445), job-lifecycle frames
 * (`JobActive`/`JobCompleted`/`JobFailed`, plus `JobProgress` for a job that
 * reports per-item progress — SKY-4468). These effects:
 * 1. authenticate the socket on open with the current access token,
 * 2. map `Invalidate` frames to RTK Query tag invalidations, so subscribed
 *    queries refetch over the authorized HTTP API (Cerbos stays in the HTTP
 *    path — no entity data crosses the socket), and
 * 3. settle tracked Telegram Groups jobs on `JobCompleted`/`JobFailed` (and
 *    record progress on `JobProgress`), invalidating the affected group (or
 *    the whole list for a bulk job like `import`) and queuing a notification
 *    — see `TradingGroupJobNotifications`.
 */

import type { UnknownAction } from '@reduxjs/toolkit';
import { startAppListening } from 'src/store/middleware/listenerMiddleware';
import type { RootState } from 'src/store';
import {
  actionAssistantOnOpen,
  actionAssistantOnMessage,
  wsAssistantSend,
  parseInvalidateMessage,
  parseJobMessage,
} from '@skynet/data-ws-assistant';
import type { InvalidateMessage, JobMessage } from '@skynet/data-ws-assistant';
import { selectAccessToken } from '@skynet/data-auth-api';
import { debounceInvalidate } from './ws-invalidation-debounce';
import type { MappedInvalidations } from './ws-invalidation-debounce';
import { exchangesApi } from '@skynet/data-exchanges';
import { botCommandsApi } from '@skynet/data-bot-commands';
import { marketingCampaignsApi } from '@skynet/data-marketing-campaigns';
import { regionsApi } from '@skynet/data-regions';
import { telegramGroupsApi } from '@skynet/data-trading-groups';
import { telegramUsersApi } from '@skynet/data-telegram-users';
import { exchangeSyncActions } from '@skynet/data-exchange-sync';
import {
  tradingGroupJobsActions,
  selectTradingGroupJobEntry,
} from '@skynet/data-trading-groups-jobs';
import type { TradingGroupJobOperation } from '@skynet/data-trading-groups-jobs';

/** List tag id used by the data packages' `provideListTags` helper. */
const LIST_TAG_ID = 'LIST';

/**
 * Upper bound for a single exchange changelog sync (usually ~1 min); purely a
 * backstop for a missed `sync-finished` event — the WS frame is the real
 * mechanism.
 */
export const SYNC_SPINNER_STALE_MS = 3 * 60 * 1000;

/**
 * Maps an `Invalidate` frame to the actions to dispatch, split into cache
 * invalidations and the sync-state updates that must not be replayed on the
 * debounce's trailing edge (see {@link MappedInvalidations}). Unknown entities
 * map to no actions (forward-compatible with new backend events).
 */
export const invalidateActionsFor = (msg: InvalidateMessage): MappedInvalidations => {
  const { entity, action, id } = msg;

  switch (entity) {
    case 'exchanges': {
      if (action === 'sync-started') {
        // A sync adds a sync-log record, so refresh the logs here instead of
        // waiting for completion. Caveat: the backend currently publishes this
        // frame *before* the row is committed (assistant-scraper's consumer),
        // so this refetch usually still returns the previous list — it becomes
        // meaningful once the backend emits the event after the write. That
        // reorder is tracked on the backend side of SKY-4529 (skynet-assistant);
        // until then `sync-finished` (which always fires, even on failure)
        // refreshes the logs again. The changelogs and the exchange row
        // genuinely only change at completion, so they stay there anyway.
        return {
          cache:
            id !== undefined
              ? [exchangesApi.util.invalidateTags([{ type: 'ExchangeSyncLogs', id }])]
              : [],
          // The spinner state is keyed by numeric exchange id; a string id
          // still refreshes the logs above (mirrors `sync-finished`)
          state: typeof id === 'number' ? [exchangeSyncActions.syncStarted(id)] : [],
        };
      }

      if (action === 'sync-finished') {
        // A finished sync produces new changelogs/synclogs and refreshes the
        // exchange row (`lastSyncedAt`/`lastChangelogAt`)
        return {
          cache: [
            exchangesApi.util.invalidateTags(
              id !== undefined
                ? [
                    { type: 'ExchangeChangelogs', id },
                    { type: 'ExchangeSyncLogs', id },
                    { type: 'Exchanges', id },
                  ]
                : ['ExchangeChangelogs', 'ExchangeSyncLogs', 'Exchanges'],
            ),
          ],
          state: typeof id === 'number' ? [exchangeSyncActions.syncFinished(id)] : [],
        };
      }

      return {
        cache: [
          exchangesApi.util.invalidateTags(
            id !== undefined
              ? [
                  { type: 'Exchanges', id },
                  { type: 'Exchanges', id: LIST_TAG_ID },
                ]
              : ['Exchanges'],
          ),
        ],
      };
    }

    case 'bot-commands':
      return {
        cache: [
          botCommandsApi.util.invalidateTags(
            id !== undefined
              ? [
                  { type: 'BotCommands', id },
                  { type: 'BotCommands', id: LIST_TAG_ID },
                ]
              : ['BotCommands'],
          ),
        ],
      };

    case 'marketing-campaigns':
      return {
        cache: [
          marketingCampaignsApi.util.invalidateTags(
            id !== undefined
              ? [
                  { type: 'MarketingCampaigns', id },
                  { type: 'MarketingCampaigns', id: LIST_TAG_ID },
                ]
              : ['MarketingCampaigns'],
          ),
        ],
      };

    case 'regions':
      return {
        cache: [
          regionsApi.util.invalidateTags(
            id !== undefined
              ? [
                  { type: 'Regions', id },
                  { type: 'Regions', id: LIST_TAG_ID },
                ]
              : ['Regions'],
          ),
        ],
      };

    case 'telegram-groups':
      return {
        cache: [
          telegramGroupsApi.util.invalidateTags(
            id !== undefined
              ? [
                  { type: 'TelegramGroups', id },
                  { type: 'TelegramGroups', id: LIST_TAG_ID },
                ]
              : ['TelegramGroups'],
          ),
        ],
      };

    // Unknown/future entities: no-op (forward-compatible with new backend events)
    default:
      return { cache: [] };
  }
};

/**
 * Upper bound for a Telegram Groups job (create can take ~60–75s, paced by
 * Telegram FLOOD_WAIT); purely a backstop for a missed `JobCompleted`/
 * `JobFailed` frame — the WS frame is the real mechanism.
 */
export const TRADING_GROUP_JOB_STALE_MS = 3 * 60 * 1000;

/**
 * Whether each job operation can change a group's Telegram roster (SKY-4627):
 * once one that can settles, `@skynet/data-telegram-users`' `add`/`remove`
 * picker rosters may be answering with stale membership. That package carries
 * no tags of its own to invalidate from its own mutations — it has none, the
 * roster only ever changes as a side effect of these — so it's invalidated
 * from here instead, alongside the `TelegramGroups` invalidation every settle
 * path below already does. `add-members`/`remove-members` are the obvious
 * ones; `archive`/`delete` (and their bulk variants) qualify too because
 * `mode=remove` only answers for a stored roster's *active* groups —
 * archiving or deleting a group can drop its members from that answer even
 * though no membership mutation ran. `mode=add`'s answer is unaffected by
 * either, but invalidating both rosters together is cheap and keeps this
 * table from having to track which mode each operation actually affects.
 *
 * A `Record` over the whole `TradingGroupJobOperation` union rather than a
 * set of the positive cases: a typo fails to compile, and so does a new
 * operation until it's classified here. `import` is the one real backend job
 * name outside that union (the dashboard never submits it, see
 * `TradingGroupJobOperation`), so it lands in `telegramUsersInvalidationFor`'s
 * unrecognised-name branch and invalidates — which is the right answer for it
 * anyway: an import backfills active group rows, and `mode=remove` answers
 * from exactly those.
 */
const MUTATES_TELEGRAM_ROSTER: Record<TradingGroupJobOperation, boolean> = {
  create: false,
  update: false,
  'change-bot': false,
  archive: true,
  delete: true,
  'bulk-archive': true,
  'bulk-delete': true,
  'add-members': true,
  'remove-members': true,
  'bulk-add-members': true,
  'bulk-remove-members': true,
};

const isTradingGroupJobOperation = (name: string): name is TradingGroupJobOperation =>
  Object.hasOwn(MUTATES_TELEGRAM_ROSTER, name);

/**
 * The `TelegramUsers` invalidation for a settled job, or none for an operation
 * that can't have changed either roster.
 *
 * Callers pass the tracked entry's typed `operation` whenever this client
 * submitted the job, and fall back to the frame's raw `name` only when it
 * didn't (another tab's job) — `JobMessage.name` is a plain `string` with no
 * enum in the API spec, so a backend rename can't be caught at compile time.
 * A name this client doesn't recognise therefore invalidates anyway: one
 * extra refetch of a mounted roster is cheap, a picker left silently stale is
 * not.
 */
const telegramUsersInvalidationFor = (operation: string): UnknownAction[] =>
  !isTradingGroupJobOperation(operation) || MUTATES_TELEGRAM_ROSTER[operation]
    ? [telegramUsersApi.util.invalidateTags([{ type: 'TelegramUsers', id: LIST_TAG_ID }])]
    : [];

/**
 * Grace window for the start/finish race: this client's own `jobStarted` is
 * dispatched only once its mutation's `202` HTTP response resolves, but a WS
 * terminal frame for that same job travels a different transport and can
 * arrive first (most likely for a job that finishes in well under a second).
 * Bounds how long an unmatched terminal frame is held before being dropped —
 * generous enough for that race, short enough that it doesn't linger given
 * frames are broadcast to every authenticated client (most buffered frames
 * are simply other users' jobs this client never submitted).
 */
export const TRADING_GROUP_JOB_RACE_MS = 10 * 1000;

/**
 * Extra grace window *after* the stale backstop has already given up on a
 * job (settled it as `unknown` and stopped tracking it): a `JobCompleted`/
 * `JobFailed` frame that is merely late — not lost — can still arrive during
 * this window, most plausibly for a `create` job whose Telegram FLOOD_WAIT
 * pacing pushed it past `TRADING_GROUP_JOB_STALE_MS`. The "status unknown"
 * toast already shown is not retroactively replaced (claiming success this
 * late risks confusing the operator more than it helps), but the cache is
 * still corrected instead of staying wrong until an unrelated refetch.
 */
export const TRADING_GROUP_JOB_LATE_FRAME_MS = 2 * 60 * 1000;

/**
 * SKY-4447: whether an incoming job frame is this admin's own, per the
 * assistant-api's audience-scoped WS delivery contract (see the Linear
 * comment on SKY-4445):
 *
 * - `initiatorId != null` — the backend only delivers this frame to the
 *   initiating admin's own connections, so it's guaranteed to be this
 *   client's job even if *this tab* isn't the one that submitted it (e.g. a
 *   second tab, or a reload after the mutation was already accepted).
 * - otherwise — falls back to this tab's own bookkeeping (`isLocallyTracked`,
 *   from `selectTradingGroupJobEntry`), for a job enqueued by an API build
 *   that predates `initiatorId` and still broadcasts to every authenticated
 *   client.
 *
 * A frame that is neither is a system job or another admin's untagged job —
 * not mine, ignored.
 */
export const isMineJobFrame = (msg: JobMessage, isLocallyTracked: boolean): boolean =>
  msg.initiatorId != null || isLocallyTracked;

/**
 * Maps a job-lifecycle frame to the actions to dispatch. Reacts only to
 * frames for a Telegram Groups job that are this admin's own — see
 * `isMineJobFrame`. A frame that isn't is either another user's broadcast
 * job (pre-SKY-4447 backend), one this client already settled, or an unknown
 * entity — all no-ops, so other users' jobs never affect this client's UI.
 *
 * `trackedOperation` is the locally tracked entry's operation, when this tab
 * submitted the job — preferred over the frame's untyped `name`, see
 * `telegramUsersInvalidationFor`.
 */
export const jobActionsFor = (
  msg: JobMessage,
  isMine: boolean,
  trackedOperation?: TradingGroupJobOperation,
): UnknownAction[] => {
  if (msg.entity !== 'telegram-groups' || !isMine) {
    return [];
  }

  switch (msg.command) {
    // The row/dialog already shows pending from the optimistic `jobStarted`
    // dispatch made when the mutation was accepted — nothing more to do.
    case 'JobActive':
      return [];

    // Terminal wins (SKY-4468): a stale/reordered JobProgress for a jobId
    // already settled (removed from `byJobId` by JobCompleted/JobFailed) still
    // reaches here when `initiatorId` makes `isMine` true regardless of local
    // tracking — but `jobProgressed` itself no-ops for an untracked jobId, the
    // same rule `jobSettled` already relies on, so it can never reopen a
    // settled job's bar.
    case 'JobProgress':
      return msg.progress
        ? [tradingGroupJobsActions.jobProgressed({ jobId: msg.jobId, progress: msg.progress })]
        : [];

    case 'JobCompleted':
      return [
        tradingGroupJobsActions.jobSettled({
          jobId: msg.jobId,
          outcome: 'completed',
          groupId: msg.resultId,
        }),
        telegramGroupsApi.util.invalidateTags(
          msg.resultId !== undefined
            ? [
                { type: 'TelegramGroups', id: msg.resultId },
                { type: 'TelegramGroups', id: LIST_TAG_ID },
              ]
            : ['TelegramGroups'],
        ),
        ...telegramUsersInvalidationFor(trackedOperation ?? msg.name),
      ];

    case 'JobFailed':
      return [
        tradingGroupJobsActions.jobSettled({
          jobId: msg.jobId,
          outcome: 'failed',
          error: msg.error,
          message: msg.message,
          code: msg.code,
          retryAfterSeconds: msg.retryAfterSeconds,
        }),
        // A `bulk-add-members`/`bulk-remove-members` job isn't atomic — the
        // backend applies membership per item and reports per-item outcomes
        // (see `MembersJobReportModal`'s `added`/`removed`/`skipped`/`failed`
        // rows), so an overall `failed` job can still have changed the
        // roster for whichever items it got to before failing.
        // `lateCorrectionActionsFor` below applies the same rule to a late
        // `JobFailed`, so the two paths never disagree.
        ...telegramUsersInvalidationFor(trackedOperation ?? msg.name),
      ];

    default:
      return [];
  }
};

/**
 * The cache-invalidation action(s) for a terminal frame that turns out to be
 * merely late — not lost — after the stale backstop already force-settled
 * the job (see `TRADING_GROUP_JOB_LATE_FRAME_MS`). Prefers the frame's own
 * `resultId` (authoritative) over `rememberedGroupId` (the backstop's own
 * best guess, taken from the mutation's `arg` at `jobStarted` time), falling
 * back to a whole-list invalidation when neither is known (e.g. a `create`
 * that failed before ever producing a group). `rememberedOperation` is the
 * force-settled entry's typed operation, preferred over the frame's untyped
 * `name` — see `telegramUsersInvalidationFor`. The roster is invalidated for
 * a late `JobFailed` too, exactly as `jobActionsFor` does for a timely one:
 * a failed member job can still have partially applied.
 */
export const lateCorrectionActionsFor = (
  msg: JobMessage,
  rememberedGroupId: number | undefined,
  rememberedOperation?: TradingGroupJobOperation,
): UnknownAction[] => {
  const groupId =
    msg.command === 'JobCompleted' && msg.resultId !== undefined ? msg.resultId : rememberedGroupId;

  return [
    telegramGroupsApi.util.invalidateTags(
      groupId !== undefined
        ? [
            { type: 'TelegramGroups', id: groupId },
            { type: 'TelegramGroups', id: LIST_TAG_ID },
          ]
        : ['TelegramGroups'],
    ),
    ...telegramUsersInvalidationFor(rememberedOperation ?? msg.name),
  ];
};

/*
 * Pulse (keepalive) — how it works today and how to add client-side liveness.
 *
 * The server is the authoritative side of the pulse: assistant-api sends a
 * PROTOCOL-LEVEL WebSocket ping every 30s. The browser answers pong
 * automatically (below the JS API — no code here is involved), and the server
 * terminates any socket that missed a pong by the next tick. Any frame,
 * including those pings, also resets nginx's proxy_read_timeout.
 *
 * This differs from the MM/ES channels, whose 1-minute client "listen"
 * command is really a data-subscription refresh — the assistant channel has
 * no subscriptions (every authenticated client receives every invalidation
 * signal), so there is nothing to "listen" to.
 *
 * The one gap: the browser does not expose missed pings, so a dead network
 * path is only noticed client-side when the TCP close finally fires (ws-core's
 * reconnectOnClose then recovers). If that lag ever matters, uncomment
 * startClientLivenessProbe() below (and its call in startWsAssistantEffects):
 * it sends an app-level {command:'Ping'} every PULSE_INTERVAL_MS — the gateway
 * already replies {command:'Pong'} — and force-reconnects when no Pong arrives.
 *
 * Additional imports needed when uncommenting:
 *   import { store } from 'src/store';
 *   import {
 *     selectWsAssistantStatus, wsAssistantConnect,
 *     wsAssistantDisconnect, subscribeWsAssistantData,
 *   } from '@skynet/data-ws-assistant';
 */
// const PULSE_INTERVAL_MS = 60_000;
//
// const startClientLivenessProbe = () => {
//   let awaitingPong = false;
//
//   // Any Pong (or any frame at all) proves the connection is alive
//   const pongAbort = startAppListening({
//     actionCreator: actionAssistantOnMessage,
//     effect: () => {
//       awaitingPong = false;
//     },
//   });
//
//   const timer = setInterval(() => {
//     const state = store.getState();
//
//     if (selectWsAssistantStatus(state) !== 'connected') {
//       awaitingPong = false;
//       return;
//     }
//
//     if (awaitingPong) {
//       // No Pong since the previous probe — assume a dead path and force a
//       // reconnect cycle (disconnect triggers ws-core's reconnectOnClose)
//       awaitingPong = false;
//       void store.dispatch(wsAssistantDisconnect()).then(() => {
//         void store.dispatch(subscribeWsAssistantData(wsAssistantConnect()));
//       });
//       return;
//     }
//
//     awaitingPong = true;
//     store.dispatch(wsAssistantSend({ command: 'Ping' }));
//   }, PULSE_INTERVAL_MS);
//
//   return () => {
//     clearInterval(timer);
//     pongAbort();
//   };
// };

export const startWsAssistantEffects = () => {
  // Authenticate the socket as soon as it opens (and on every reconnect) with
  // a fresh access token from the store — same source the HTTP clients use.
  //
  // Auth failure needs no handling here: the server replies with an error
  // frame and closes the socket with 4401, ws-core's reconnectOnClose then
  // reconnects, and this listener re-auths with whatever token is in the
  // store at that moment (fresh after a token refresh).
  const authOnOpenAbort = startAppListening({
    actionCreator: actionAssistantOnOpen,
    effect: (_action, listenerApi) => {
      const token = selectAccessToken(listenerApi.getState());

      if (token) {
        listenerApi.dispatch(wsAssistantSend({ command: 'Auth', token }));
      }
    },
  });

  // Uncomment along with startClientLivenessProbe above for client-side pulse:
  // const livenessAbort = startClientLivenessProbe();

  const invalidateOnMessageAbort = startAppListening({
    actionCreator: actionAssistantOnMessage,
    effect: (action, listenerApi) => {
      const msg = parseInvalidateMessage(action.payload);

      if (!msg) {
        return;
      }

      debounceInvalidate('assistant', msg, invalidateActionsFor(msg), listenerApi.dispatch);
    },
  });

  // Stale-spinner safety: if the `sync-finished` event never arrives (WS
  // dropped mid-sync — frames are not replayed), clear the syncing flag after
  // a generous upper bound instead of leaving the spinner stuck forever.
  const staleTimers = new Map<number, ReturnType<typeof setTimeout>>();

  const staleSpinnerStartAbort = startAppListening({
    actionCreator: exchangeSyncActions.syncStarted,
    effect: (action, listenerApi) => {
      const id = action.payload;

      clearTimeout(staleTimers.get(id));
      staleTimers.set(
        id,
        setTimeout(() => {
          staleTimers.delete(id);
          listenerApi.dispatch(exchangeSyncActions.syncFinished(id));
          // The sync-finished frame was missed (WS drop — frames are not
          // replayed), so the sync most likely completed without us hearing
          // it: refetch the affected caches so the data is merely late, not
          // stale until the next unrelated event or a manual reload
          listenerApi.dispatch(
            exchangesApi.util.invalidateTags([
              { type: 'ExchangeChangelogs', id },
              { type: 'ExchangeSyncLogs', id },
              { type: 'Exchanges', id },
            ]),
          );
        }, SYNC_SPINNER_STALE_MS),
      );
    },
  });

  const staleSpinnerFinishAbort = startAppListening({
    actionCreator: exchangeSyncActions.syncFinished,
    effect: (action) => {
      clearTimeout(staleTimers.get(action.payload));
      staleTimers.delete(action.payload);
    },
  });

  // Start/finish race backstop (see `TRADING_GROUP_JOB_RACE_MS`): a terminal
  // frame for a jobId this client isn't tracking yet is buffered here instead
  // of being dropped outright, in case it's this client's own job whose
  // `jobStarted` just hasn't landed yet — the `jobStarted` listener below
  // replays it the moment tracking starts. Frames are never replayed by the
  // server, so once a frame is missed here it's gone for good.
  const pendingTerminalFrames = new Map<string, JobMessage>();
  const pendingTerminalTimers = new Map<string, ReturnType<typeof setTimeout>>();

  const bufferPendingTerminalFrame = (
    msg: JobMessage,
    dispatch: (action: UnknownAction) => unknown,
  ) => {
    pendingTerminalFrames.set(msg.jobId, msg);
    clearTimeout(pendingTerminalTimers.get(msg.jobId));
    pendingTerminalTimers.set(
      msg.jobId,
      setTimeout(() => {
        pendingTerminalFrames.delete(msg.jobId);
        pendingTerminalTimers.delete(msg.jobId);

        if (msg.initiatorId != null) {
          // Audience-scoped delivery (SKY-4447) guarantees this frame is
          // mine even though *this tab* never submitted it (e.g. another
          // tab/session of the same admin) — `jobStarted` will therefore
          // never fire here to replay it. Act on it directly instead of
          // silently dropping it now that the same-tab race window has
          // elapsed.
          for (const jobAction of jobActionsFor(msg, true)) {
            dispatch(jobAction);
          }
        }
      }, TRADING_GROUP_JOB_RACE_MS),
    );
  };

  // Late-frame correction (see `TRADING_GROUP_JOB_LATE_FRAME_MS`): remembers,
  // for a bounded window, which jobIds the stale backstop already force-
  // settled — so a real terminal frame that turns out to be merely late can
  // still fix the cache instead of being buffered forever waiting for a
  // `jobStarted` that already fired long ago and will never fire again.
  const forceSettledJobs = new Map<
    string,
    { groupId: number | undefined; operation: TradingGroupJobOperation }
  >();
  const forceSettledTimers = new Map<string, ReturnType<typeof setTimeout>>();

  const rememberForceSettled = (
    jobId: string,
    groupId: number | undefined,
    operation: TradingGroupJobOperation,
  ) => {
    forceSettledJobs.set(jobId, { groupId, operation });
    clearTimeout(forceSettledTimers.get(jobId));
    forceSettledTimers.set(
      jobId,
      setTimeout(() => {
        forceSettledJobs.delete(jobId);
        forceSettledTimers.delete(jobId);
      }, TRADING_GROUP_JOB_LATE_FRAME_MS),
    );
  };

  // Telegram Groups job-lifecycle frames — same transport as `Invalidate`
  // (SKY-4445), parsed and dispatched independently: `parseJobMessage`
  // returns `null` for any other frame, so this listener and the one above
  // simply ignore whichever shape isn't theirs.
  const jobOnMessageAbort = startAppListening({
    actionCreator: actionAssistantOnMessage,
    effect: (action, listenerApi) => {
      const msg = parseJobMessage(action.payload);

      if (!msg) {
        return;
      }

      const trackedEntry = selectTradingGroupJobEntry(listenerApi.getState(), msg.jobId);
      const isLocallyTracked = Boolean(trackedEntry);
      const isMine = isMineJobFrame(msg, isLocallyTracked);

      if (
        !isLocallyTracked &&
        msg.entity === 'telegram-groups' &&
        (msg.command === 'JobCompleted' || msg.command === 'JobFailed')
      ) {
        const forceSettled = forceSettledJobs.get(msg.jobId);

        if (forceSettled) {
          // Merely late, not lost: the stale backstop already told the
          // operator we lost track of this one. Correct the cache instead of
          // leaving it stale until an unrelated refetch.
          forceSettledJobs.delete(msg.jobId);
          clearTimeout(forceSettledTimers.get(msg.jobId));
          forceSettledTimers.delete(msg.jobId);

          for (const correctionAction of lateCorrectionActionsFor(
            msg,
            forceSettled.groupId,
            forceSettled.operation,
          )) {
            listenerApi.dispatch(correctionAction);
          }
          return;
        }

        bufferPendingTerminalFrame(msg, listenerApi.dispatch);
        return;
      }

      for (const jobAction of jobActionsFor(msg, isMine, trackedEntry?.operation)) {
        listenerApi.dispatch(jobAction);
      }
    },
  });

  // Resolves the race: once this client's own `jobStarted` lands for a jobId
  // whose terminal frame already arrived (buffered above), replay it
  // immediately rather than waiting for a frame that will never come again.
  const jobRaceResolveAbort = startAppListening({
    actionCreator: tradingGroupJobsActions.jobStarted,
    effect: (action, listenerApi) => {
      const { jobId, operation } = action.payload;
      const pendingMsg = pendingTerminalFrames.get(jobId);

      if (!pendingMsg) {
        return;
      }

      pendingTerminalFrames.delete(jobId);
      clearTimeout(pendingTerminalTimers.get(jobId));
      pendingTerminalTimers.delete(jobId);

      for (const jobAction of jobActionsFor(pendingMsg, true, operation)) {
        listenerApi.dispatch(jobAction);
      }
    },
  });

  // Stale-job safety: if a job's `JobCompleted`/`JobFailed` frame never
  // arrives (WS dropped mid-job — frames are not replayed), settle it after a
  // generous upper bound instead of leaving the row/dialog pending forever.
  //
  // "Stale" means a bounded window of *silence*, not a bounded total job
  // duration: `armStaleJobTimer` is called both on `jobStarted` and on every
  // subsequent `jobProgressed` (below), each call re-arming the same window.
  // A single-group job (create/update/archive/delete) never reports progress,
  // so for it this is unchanged from before — one window from start to
  // settle. A `bulk-archive`/`bulk-delete` job (SKY-4484) can legitimately run
  // ~25 minutes, well past `TRADING_GROUP_JOB_STALE_MS`, but keeps emitting
  // `JobProgress` as it works through the batch — without the re-arm, this
  // backstop would falsely declare "status unknown" a few minutes into a
  // batch that's progressing fine.
  const staleJobTimers = new Map<string, ReturnType<typeof setTimeout>>();

  const armStaleJobTimer = (
    jobId: string,
    groupId: number | undefined,
    listenerApi: { getState: () => RootState; dispatch: (action: UnknownAction) => unknown },
  ) => {
    clearTimeout(staleJobTimers.get(jobId));
    staleJobTimers.set(
      jobId,
      setTimeout(() => {
        staleJobTimers.delete(jobId);

        // Already settled by a real frame by the time this fires — e.g. the
        // start/finish race replay above dispatches `jobSettled` for the
        // very `jobStarted` this timer was armed from, in the same tick,
        // before this listener (registered after `jobRaceResolveAbort`) gets
        // its turn. Without this guard the timer would still be armed for a
        // job that's already gone, firing a pointless refetch later.
        const entry = selectTradingGroupJobEntry(listenerApi.getState(), jobId);

        if (!entry) {
          return;
        }

        // Settle as `unknown`, not `completed` — the frame was missed, so
        // the job's actual outcome (success or failure) was never observed.
        // Claiming success here would both lie to the operator and, if the
        // real frame is merely late rather than lost, permanently swallow it
        // (this timer already deleted the tracked entry, so a subsequent
        // frame for this jobId is no longer tracked and gets ignored).
        listenerApi.dispatch(tradingGroupJobsActions.jobSettled({ jobId, outcome: 'unknown' }));
        // Refetch the affected cache anyway so the data is merely late, not
        // stale until the next unrelated event or a reload
        listenerApi.dispatch(
          telegramGroupsApi.util.invalidateTags(
            groupId !== undefined
              ? [
                  { type: 'TelegramGroups', id: groupId },
                  { type: 'TelegramGroups', id: LIST_TAG_ID },
                ]
              : ['TelegramGroups'],
          ),
        );
        for (const invalidation of telegramUsersInvalidationFor(entry.operation)) {
          listenerApi.dispatch(invalidation);
        }
        // Give a real (merely late, not lost) frame a further bounded
        // window to correct the cache — see `TRADING_GROUP_JOB_LATE_FRAME_MS`.
        rememberForceSettled(jobId, groupId, entry.operation);
      }, TRADING_GROUP_JOB_STALE_MS),
    );
  };

  const staleJobStartAbort = startAppListening({
    actionCreator: tradingGroupJobsActions.jobStarted,
    effect: (action, listenerApi) => {
      const { jobId, groupId } = action.payload;
      armStaleJobTimer(jobId, groupId, listenerApi);
    },
  });

  // Re-arms the same window on every progress report — see the comment above
  // `staleJobTimers`. A jobId this client isn't tracking (unknown, already
  // settled, or another user's job) has no entry to read `groupId` from, so
  // there is nothing to re-arm; `jobProgressed`'s own reducer already no-ops
  // for it too.
  const staleJobProgressAbort = startAppListening({
    actionCreator: tradingGroupJobsActions.jobProgressed,
    effect: (action, listenerApi) => {
      const { jobId } = action.payload;
      const entry = selectTradingGroupJobEntry(listenerApi.getState(), jobId);

      if (!entry) {
        return;
      }

      armStaleJobTimer(jobId, entry.groupId, listenerApi);
    },
  });

  const staleJobSettleAbort = startAppListening({
    actionCreator: tradingGroupJobsActions.jobSettled,
    effect: (action) => {
      clearTimeout(staleJobTimers.get(action.payload.jobId));
      staleJobTimers.delete(action.payload.jobId);
    },
  });

  return () => {
    authOnOpenAbort();
    invalidateOnMessageAbort();
    staleSpinnerStartAbort();
    staleSpinnerFinishAbort();
    for (const timer of staleTimers.values()) {
      clearTimeout(timer);
    }
    staleTimers.clear();
    jobOnMessageAbort();
    jobRaceResolveAbort();
    for (const timer of pendingTerminalTimers.values()) {
      clearTimeout(timer);
    }
    pendingTerminalTimers.clear();
    pendingTerminalFrames.clear();
    for (const timer of forceSettledTimers.values()) {
      clearTimeout(timer);
    }
    forceSettledTimers.clear();
    forceSettledJobs.clear();
    staleJobStartAbort();
    staleJobProgressAbort();
    staleJobSettleAbort();
    for (const timer of staleJobTimers.values()) {
      clearTimeout(timer);
    }
    staleJobTimers.clear();
    // livenessAbort();
  };
};
